<template>
  <div
    data-component-group="business-form"
    data-component="cpm-prices"
  >
    <slot name="beforeForm" />

    <div
      v-if="!isDisabled"
      class="component-header"
    >
      <alerts-base
        class="business-form--alert"
      >
        <template #text>
          <span class="bold">{{ $t('please_note') }} </span>
          <span class="c-text-secondary">{{ $t('cpm_prices_set_in_this_section_will_overwrite') }}</span>
        </template>
      </alerts-base>

      <magic-container>
        <v-autocomplete
          v-model="advertiser"
          :items="businessAdvertisers"
          :label="$t('advertiser')"
          item-title="name"
          item-value="id"
          :return-object="true"
          :readonly="isDisabled"
          required
          :error-messages="advertiserErrors"
          @blur="v$.advertiser.$touch()"
        />

        <v-autocomplete
          v-model="country"
          :items="enabledPromotionsCountries"
          prepend-inner-icon="public"
          :label="$t('country')"
          item-title="name"
          item-value="code"
          :return-object="true"
          required
          :readonly="isDisabled"
          :error-messages="countryErrors"
          @update:model-value="onChangeCountry"
          @blur="v$.country.$touch()"
        />
        <v-text-field
          class="price-select"
          v-model.number="price"
          :label="$t('price')"
          :suffix="currency"
          :error-messages="priceErrors"
          required
          autocomplete="off"
          @blur="v$.price.$touch()"
        />
      </magic-container>

      <div class="component-actions">
        <Can
          action="update"
          subject="business"
        >
          <v-btn
            color="secondary"
            :loading="isLoading"
            @click="onUpdate"
          >
            <v-icon>edit</v-icon>
            {{ triggerLabel }}
          </v-btn>
        </Can>
      </div>
    </div>
    <div class="component-body">
      <div class="item-grid-rows" :class="{ 'item-grid-rows--edit': !isRemovableDisabled }">
      <Removables
        v-if="hasItems"
        :items="businessCpmPrices"
        :is-disabled="isRemovableDisabled"
        :on-remove="onRemoveBusinessPrice"
      >
        <template
          v-slot:item="scope"

        >
          <div class="item-grid">
            <span class="d-flex align-center">
              <v-icon>public</v-icon>
              <span>{{ scope.item.country.name }}</span>
            </span>

            <span>
              {{ scope.item.advertiser.name }}
            </span>

            <span>
              {{ scope.item.price }} {{ scope.item.country.currency.code }}
            </span>
          </div>
        </template>
      </Removables>
      </div>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import Removables from '../../lists/Removables';
  import { isStr, isNum } from '../../../utils';

  export default {
    components: {
      Removables
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        country: {
          required
        },
        advertiser: {
          required
        },
        price: {
          required,
          numeric: {
            $validator: value => isNum(value),
            $message: 'Must be a valid number'
          },
          minValue: {
            $validator: value => value >= 1,
            $message: 'Must be greater than 0'
          }
        }
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined
      },
      triggerLabel: {
        type: String,
        default: 'Add'
      },
      isDisabled: {
        type: Boolean,
        default: false
      },
      onSubmit: {
        type: Function,
        default: () => {}
      }
    },
    data() {
      return {
        country: null,
        advertiser: null,
        price: '',
        currency: 'CPM',
        isLoading: false,
        isLoadingCountries: false,
      };
    },
    computed: {
      userCountries() {
        return this.$store.getters['users/countries'];
      },
      activeBusiness() {
        return this.$store.getters['businesses/activeItem'];
      },
      businessAdvertisers() {
        return this.activeBusiness?.businessAdvertisers ?? [];
      },
      businessCpmPrices() {
        return this.activeBusiness?.businessCpmPrices ?? [];
      },
      enabledPromotionsCountries() {
        const enabledPromotions = this.activeBusiness?.enabledPromotions ?? [];

        return this.userCountries.filter((item) => {
          return enabledPromotions.find(promotionCountry => item.code === promotionCountry.country.code);
        });
      },
      hasItems() {
        return this.businessCpmPrices.length > 0;
      },
      canUpdateBusiness() {
        return this.$ability.can('update', 'business');
      },
      isRemovableDisabled() {
        return this.isDisabled || !this.canUpdateBusiness;
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('country_is_required');
          }
          return error.$message;
        });
      },
      advertiserErrors() {
        return this.v$.advertiser.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('advertiser_is_required');
          }
          return error.$message;
        });
      },
      priceErrors() {
        return this.v$.price.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('price_is_required');
          }
          if (error.$validator === 'numeric' || error.$validator === 'minValue') {
            return this.$t('price_must_be_a_positive');
          }
          return error.$message;
        });
      },
      hasFormErrors() {
        const { countryErrors, advertiserErrors, priceErrors } = this;
        return countryErrors.length > 0
          || advertiserErrors.length > 0
          || priceErrors.length > 0;
      }
    },
    watch: {
      'business.id': {
        immediate: true,
        handler(nextId, prevId) {
          if (nextId) {
            if (prevId !== undefined) {
              this.clear();
            }
            this.getBusinessCpmPrices(this.business);
          }
        }
      },
    },
    methods: {
      getBusinessCpmPrices({ id }) {
        const query = { id };
        return this.$store.dispatch('businesses/getBusinessCpmPrices', query);
      },
      getBusinessAdvertisers() {
        const { id } = this.business;
        const query = {
          id
        };

        return this.$store.dispatch('businesses/getBusinessAdvertisers', query);
      },
      getEnabledPromotionsCountries({ id }) {
        const query = {
          id,
          only_allowed_countries: true,
        };
        return this.$store.dispatch('businesses/getEnabledPromotions', query);
      },
      canSubmitRequest() {
        return isStr(this.country?.code)
          && isNum(this.business?.id)
          && isNum(this.advertiser?.id)
          && isNum(this.price)
          && !this.hasFormErrors;
      },
      onChangeCountry(item) {
        this.currency = item.currency.code;
      },
      onUpdate() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.isLoading = true;
        const { business, country, advertiser, price } = this;
        const query = {
          id: business.id,
          country: country.code,
          advertiser,
          price
        };

        this.onSubmit(query)
          .then((res) => {
            this.clear();
            return res;
          })
          .catch(err => err)
          .finally(() => {
            this.isLoading = false;
          })
      },
      onRemoveBusinessPrice(item) {
        const query = {
          id: this.business.id,
          price: item
        };

        return this.$store.dispatch('businesses/removeBusinessCpmPrice', query);
      },
      clear() {
        this.v$.$reset();
        this.country = null;
        this.advertiser = null;
        this.price = '';
        this.currency = 'CPM';
        this.isLoadingCountries = false;
      }
    }
  };
</script>

<style lang="scss">
@use 'sass:map';
  @use '../../../assets/styles/theme' as *;

  [data-component-group="business-form"][data-component="cpm-prices"] {
    padding: $spacing-xl;
    background-color: rgb(var(--v-theme-surface));
    border-top: 1px solid map.get($colors, grey, lighten-2);

    .business-form--alert {
      margin-bottom: 24px;
    }

    .price-select .v-text-field__suffix {
      color: #999;
    }

    .component-actions {
      padding: 6px 0 16px 0;
      text-align: right;

      .v-btn {
        margin: 0;

        .v-icon {
          margin: 0 6px 0 0;
        }
      }
    }

    [data-component="removables"] {
      padding: 16px 0 0 0;

      .v-list {
        padding: 0;
      }

      .v-list-item {
        height: 62px;
        border-radius: $border-radius;
        background-color: rgba(var(--v-theme-on-surface), 0.06);
      }
    }
  }
</style>
